import type { FormDefinitionProblemModel } from './form-definition';
import { FORM_SHOW_WHEN_NO, normalizeFormShowWhen } from './form-show-when';
import {
	ACCESS_PROOF_KINDS,
	type AccessProofKind,
	type FormAccessProofOptionsModel,
	type FormFieldDefinitionModel,
} from './form-types';

/**
 * PD-17590 — access requests on the Registration form (docs/claude/forms/06-access-requests.md).
 *
 * A Registration form can carry an opt-in branch for people at an EXISTING customer: a
 * yes/no "Do you have an account with us?", a customer number, and a proof block. On the
 * storefront, a Yes is an access request instead of a registration: the customer number
 * names the customer, and the proofs (checked on the server) decide whether a storefront
 * user is created straight away (verified) or the customer's salesperson is asked to
 * follow up (unverified). The visitor is never told whether a customer number exists.
 *
 * Pure and dependency-free, so the settings screen, the storefront and crm-api read the
 * settings, the proofs typed and the stored outcome the same way.
 */

/** Proofs printed on documents many people see; the settings screen labels them weak. */
export const WEAK_ACCESS_PROOF_KINDS: ReadonlyArray<AccessProofKind> = [
	'post_code',
	'invoice_number',
	'order_number',
];

/** How the settings screen and the storefront name each proof. */
export const ACCESS_PROOF_LABELS: Readonly<Record<AccessProofKind, string>> = {
	email_code: 'Email code',
	phone_code: 'Phone code',
	invoice_totals: 'Recent invoice totals',
	order_totals: 'Recent order totals',
	invoice_number: 'Invoice number',
	order_number: 'Order number',
	post_code: 'Billing ZIP code',
};

/** The proofs that compare amounts and need a lookback window and a count. */
export const ACCESS_PROOF_TOTALS_KINDS: ReadonlyArray<AccessProofKind> = [
	'invoice_totals',
	'order_totals',
];

/** The proofs whose value is a code Workd sends to the customer's contact on file. */
export const ACCESS_PROOF_CODE_KINDS: ReadonlyArray<AccessProofKind> = [
	'email_code',
	'phone_code',
];

export const ACCESS_PROOF_LOOKBACK_MIN_DAYS = 7;
export const ACCESS_PROOF_LOOKBACK_MAX_DAYS = 365;
export const ACCESS_PROOF_LOOKBACK_DEFAULT_DAYS = 90;

/** A code is six digits and works for ten minutes. */
export const ACCESS_CODE_LENGTH = 6;
export const ACCESS_CODE_TTL_SECONDS = 10 * 60;

/**
 * Failed attempts per brand and customer number before the form stops taking more for a
 * while: the same three-in-fifteen-minutes window the phone agent's verify tool and phone
 * sign-in use.
 */
export const ACCESS_REQUEST_MAX_FAILED_ATTEMPTS = 3;
export const ACCESS_REQUEST_LOCK_SECONDS = 15 * 60;

/** Longest customer number, invoice or order number and ZIP code a visitor may type. */
export const ACCESS_PROOF_TEXT_MAX_LENGTH = 64;

export const DEFAULT_ACCESS_PROOF_OPTIONS: Readonly<
	Omit<FormAccessProofOptionsModel, 'customerNumberFieldKey'>
> = {
	proofs: ['email_code', 'invoice_totals'],
	required: 1,
	totalsCount: 1,
	lookbackDays: ACCESS_PROOF_LOOKBACK_DEFAULT_DAYS,
	verifyWithProofs: true,
};

/**
 * A proof block's settings read into exactly the stored shape. Unknown proofs are dropped,
 * numbers outside their ranges fall back to the defaults; what is left is judged by
 * checkAccessProofField (at least one proof, no more required than offered, the customer
 * number question on the form). Null when the value is not settings at all.
 */
export function normalizeAccessProofOptions(
	raw: unknown,
): FormAccessProofOptionsModel | null {
	if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
		return null;
	}
	const input = raw as Record<string, unknown>;
	const listed = Array.isArray(input['proofs']) ? input['proofs'] : [];
	// In the order given, each once: the page lists them as the admin ordered them.
	const proofs: Array<AccessProofKind> = [];
	for (const value of listed) {
		if (
			(ACCESS_PROOF_KINDS as ReadonlyArray<unknown>).includes(value) &&
			!proofs.includes(value as AccessProofKind)
		) {
			proofs.push(value as AccessProofKind);
		}
	}
	const lookback = input['lookbackDays'];
	return {
		customerNumberFieldKey:
			typeof input['customerNumberFieldKey'] === 'string'
				? input['customerNumberFieldKey'].trim()
				: '',
		proofs,
		required: input['required'] === 2 ? 2 : 1,
		totalsCount: input['totalsCount'] === 2 ? 2 : 1,
		lookbackDays:
			typeof lookback === 'number' &&
			Number.isInteger(lookback) &&
			lookback >= ACCESS_PROOF_LOOKBACK_MIN_DAYS &&
			lookback <= ACCESS_PROOF_LOOKBACK_MAX_DAYS
				? lookback
				: DEFAULT_ACCESS_PROOF_OPTIONS.lookbackDays,
		// Only an explicit false turns it off: forms stored before the switch take proofs.
		verifyWithProofs: input['verifyWithProofs'] !== false,
	};
}

/**
 * The settings a page and the server work with right now: the stored settings less the
 * proofs the tenant cannot offer at the moment (a phone code without a Twilio sub-account),
 * with no more proofs required than are left. With nothing left to offer, every request is
 * unverified (one proof is still required and none can pass). With Create logins with proof
 * off nothing is offered at all, so the page asks for no proofs and Send code sends nothing.
 */
export function effectiveAccessProofOptions(
	options: FormAccessProofOptionsModel,
	available: { phoneCode: boolean },
): FormAccessProofOptionsModel {
	const proofs = options.verifyWithProofs
		? options.proofs.filter(
				(kind) => kind !== 'phone_code' || available.phoneCode,
			)
		: [];
	const required = Math.max(1, Math.min(options.required, proofs.length)) as
		| 1
		| 2;
	return { ...options, proofs, required };
}

/**
 * The checks on a proof block (run by checkFormDefinition): settings that make sense, a
 * customer number question asked before it, no answer of its own (never required, no
 * default, says nothing about the sender) and at most one per form. Exported so the field
 * editor shows the same problems the server refuses.
 */
export function checkAccessProofField(
	fields: ReadonlyArray<FormFieldDefinitionModel>,
	index: number,
	at = `fields[${index}]`,
): Array<FormDefinitionProblemModel> {
	const field = fields[index];
	if (field == null) {
		return [];
	}
	const raw = field.fieldOptions?.accessProof ?? null;
	if (field.fieldType !== 'accessProof') {
		return raw != null
			? [
					{
						path: `${at}.fieldOptions.accessProof`,
						message: 'Only a proof block has proof settings',
					},
				]
			: [];
	}

	const problems: Array<FormDefinitionProblemModel> = [];
	const firstBlock = fields.findIndex(
		(candidate) => candidate.fieldType === 'accessProof',
	);
	if (firstBlock !== index) {
		problems.push({
			path: `${at}.fieldType`,
			message: 'A form can have only one proof block',
		});
	}
	if (field.isRequired) {
		problems.push({
			path: `${at}.isRequired`,
			message:
				'A proof block is never required: a request without proofs goes to the salesperson to confirm',
		});
	}

	const options = normalizeAccessProofOptions(raw);
	if (options == null) {
		problems.push({
			path: `${at}.fieldOptions.accessProof`,
			message: 'Choose the proofs this form offers',
		});
		return problems;
	}
	// With Create logins with proof off the proofs are not used, so none need be chosen.
	if (options.verifyWithProofs) {
		if (options.proofs.length === 0) {
			problems.push({
				path: `${at}.fieldOptions.accessProof.proofs`,
				message: 'Offer at least one proof',
			});
		} else if (options.required > options.proofs.length) {
			problems.push({
				path: `${at}.fieldOptions.accessProof.required`,
				message: 'Require no more proofs than the form offers',
			});
		}
	}

	const numberIndex = fields.findIndex(
		(candidate) => candidate.fieldKey === options.customerNumberFieldKey,
	);
	const numberField = fields[numberIndex];
	if (numberField == null) {
		problems.push({
			path: `${at}.fieldOptions.accessProof.customerNumberFieldKey`,
			message:
				'The proof block needs the customer number question, which is not on this form',
		});
	} else if (numberIndex > index) {
		problems.push({
			path: `${at}.fieldOptions.accessProof.customerNumberFieldKey`,
			message: 'Ask for the customer number before the proof block',
		});
	} else if (numberField.fieldType !== 'text') {
		problems.push({
			path: `${at}.fieldOptions.accessProof.customerNumberFieldKey`,
			message: 'The customer number question must be a short text question',
		});
	}
	return problems;
}

/** The access request questions of a form: the proof block and what it relies on. */
export interface AccessRequestFieldsModel {
	/**
	 * "Do you have an account with us?" (the proof block's Show only when): a single choice
	 * of Yes or No, or a yes/no switch on a form set up before it became one.
	 */
	choiceField: FormFieldDefinitionModel | null;
	customerNumberField: FormFieldDefinitionModel | null;
	proofField: FormFieldDefinitionModel;
	options: FormAccessProofOptionsModel;
}

/**
 * The form's access request questions, or null when it has no proof block (access
 * requests are off). Forgiving, like the other readers of stored versions: a question it
 * names that is missing reads as null.
 */
export function accessRequestFieldsOf(
	fields: ReadonlyArray<FormFieldDefinitionModel>,
): AccessRequestFieldsModel | null {
	const proofField = fields.find((field) => field.fieldType === 'accessProof');
	if (proofField == null) {
		return null;
	}
	const options =
		normalizeAccessProofOptions(proofField.fieldOptions?.accessProof ?? null) ??
		({
			...DEFAULT_ACCESS_PROOF_OPTIONS,
			proofs: [...DEFAULT_ACCESS_PROOF_OPTIONS.proofs],
			customerNumberFieldKey: '',
		} satisfies FormAccessProofOptionsModel);
	const rule = normalizeFormShowWhen(proofField.showWhen ?? null);
	const choiceField =
		rule != null
			? (fields.find(
					(field) =>
						field.fieldKey === rule.fieldKey &&
						(field.fieldType === 'select' || field.fieldType === 'boolean'),
				) ?? null)
			: null;
	const customerNumberField =
		fields.find((field) => field.fieldKey === options.customerNumberFieldKey) ??
		null;
	return { choiceField, customerNumberField, proofField, options };
}

/**
 * The questions a website draws. Workd treats every website submission as a registration
 * (its answer to "Do you have an account with us?" is always No), so a website is sent the
 * form without that question, the proof block and anything asked only for Yes, and the
 * questions waiting on the answer show straight away, as they would for No. A question
 * whose Show only when names one left out goes too. No access requests: unchanged.
 */
export function registrationOnlyFields<T extends FormFieldDefinitionModel>(
	fields: ReadonlyArray<T>,
): Array<T> {
	const access = accessRequestFieldsOf(fields);
	if (access == null) {
		return [...fields];
	}
	const choiceKey = access.choiceField?.fieldKey ?? null;
	const dropped = new Set<string>([access.proofField.fieldKey]);
	if (choiceKey != null) {
		dropped.add(choiceKey);
	}
	const kept: Array<T> = [];
	// A question's rule names an earlier question, so one pass in order catches chains.
	for (const field of [...fields].sort((a, b) => a.sortOrder - b.sortOrder)) {
		if (dropped.has(field.fieldKey)) {
			continue;
		}
		const rule = normalizeFormShowWhen(field.showWhen ?? null);
		if (rule == null) {
			kept.push(field);
		} else if (rule.fieldKey !== choiceKey) {
			if (dropped.has(rule.fieldKey)) {
				dropped.add(field.fieldKey);
			} else {
				kept.push(field);
			}
		} else if (rule.values.includes(FORM_SHOW_WHEN_NO)) {
			kept.push({ ...field, showWhen: null });
		} else {
			dropped.add(field.fieldKey);
		}
	}
	return kept;
}

// ─── What the visitor sends ────────────────────────────────────────────────

/**
 * PD-17590 — the proofs a visitor typed, sent in the submit body's `accessProof` key and
 * never in `answers`: answers are emailed, exported and printed, proofs must not be.
 * Totals are typed amounts ("1,234.56"), compared to the cent.
 */
export interface AccessProofInputModel {
	emailCode?: string;
	phoneCode?: string;
	invoiceTotals?: Array<string>;
	orderTotals?: Array<string>;
	invoiceNumber?: string;
	orderNumber?: string;
	postCode?: string;
}

const PROOF_TEXT_KEYS = [
	'emailCode',
	'phoneCode',
	'invoiceNumber',
	'orderNumber',
	'postCode',
] as const;

/**
 * The proofs as sent, without trusting their shape: text only, trimmed, capped, and at
 * most two totals of each kind. Anything else is left out.
 */
export function readAccessProofInput(raw: unknown): AccessProofInputModel {
	if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
		return {};
	}
	const input = raw as Record<string, unknown>;
	const text = (value: unknown): string | null => {
		if (typeof value !== 'string' && typeof value !== 'number') {
			return null;
		}
		const trimmed = String(value)
			.replace(/[\u0000-\u001F\u007F]/g, '')
			.trim()
			.slice(0, ACCESS_PROOF_TEXT_MAX_LENGTH);
		return trimmed === '' ? null : trimmed;
	};
	const proof: AccessProofInputModel = {};
	for (const key of PROOF_TEXT_KEYS) {
		const value = text(input[key]);
		if (value != null) {
			proof[key] = value;
		}
	}
	for (const key of ['invoiceTotals', 'orderTotals'] as const) {
		const list = input[key];
		if (Array.isArray(list)) {
			const amounts = list
				.slice(0, 2)
				.map(text)
				.filter((value): value is string => value != null);
			if (amounts.length > 0) {
				proof[key] = amounts;
			}
		}
	}
	return proof;
}

/** The offered proofs the visitor gave a value for, in the form's order. */
export function suppliedAccessProofs(
	input: AccessProofInputModel,
	options: Pick<FormAccessProofOptionsModel, 'proofs'>,
): Array<AccessProofKind> {
	return options.proofs.filter((kind) => {
		switch (kind) {
			case 'email_code':
				return input.emailCode != null;
			case 'phone_code':
				return input.phoneCode != null;
			case 'invoice_totals':
				return (input.invoiceTotals ?? []).length > 0;
			case 'order_totals':
				return (input.orderTotals ?? []).length > 0;
			case 'invoice_number':
				return input.invoiceNumber != null;
			case 'order_number':
				return input.orderNumber != null;
			case 'post_code':
				return input.postCode != null;
			default:
				return false;
		}
	});
}

/** A customer number as compared: trimmed, at most the stored length. */
export function normalizeCustomerNumber(value: unknown): string | null {
	if (typeof value !== 'string' && typeof value !== 'number') {
		return null;
	}
	const trimmed = String(value).trim().slice(0, ACCESS_PROOF_TEXT_MAX_LENGTH);
	return trimmed === '' ? null : trimmed;
}

/**
 * A typed amount in whole cents: "$1,234.56", "1234.5" and "1234" read as money; more than
 * two decimals, a negative amount or anything else that is not money reads as null, so an
 * unusable total can never match by rounding.
 */
export function parseAmountToCents(value: string): number | null {
	const cleaned = value.replace(/[$,\s]/g, '');
	const match = /^(\d{1,10})(?:\.(\d{1,2}))?$/.exec(cleaned);
	if (match == null) {
		return null;
	}
	const whole = Number(match[1]);
	const fraction = match[2] == null ? 0 : Number(match[2].padEnd(2, '0'));
	return whole * 100 + fraction;
}

/** A stored decimal ("1234.50") in whole cents, or null. */
export function storedAmountToCents(
	value: string | number | null | undefined,
): number | null {
	if (value == null) {
		return null;
	}
	const number = typeof value === 'number' ? value : Number(value);
	return Number.isFinite(number) ? Math.round(number * 100) : null;
}

/**
 * Whether every claimed total is the total of a DIFFERENT document, to the cent. Two equal
 * claims need two documents with that total; one document never satisfies two claims.
 */
export function totalsMatchDistinctDocuments(
	claimedCents: ReadonlyArray<number>,
	documents: ReadonlyArray<{ id: number; cents: number | null }>,
): boolean {
	if (claimedCents.length === 0) {
		return false;
	}
	const used = new Set<number>();
	for (const claim of claimedCents) {
		const document = documents.find(
			(candidate) =>
				candidate.cents != null &&
				candidate.cents === claim &&
				!used.has(candidate.id),
		);
		if (document == null) {
			return false;
		}
		used.add(document.id);
	}
	return true;
}

/**
 * Whether a typed ZIP or post code is the one on file. Case, spaces and dashes are
 * ignored; a US ZIP matches on its first five digits, so "12345" matches "12345-6789".
 */
export function postCodeMatches(
	typed: string,
	onFile: string | null | undefined,
): boolean {
	const normalize = (value: string): string =>
		value.toUpperCase().replace(/[\s-]/g, '');
	const a = normalize(typed);
	const b = normalize(onFile ?? '');
	if (a === '' || b === '') {
		return false;
	}
	const isZip = (value: string): boolean => /^\d{5}(\d{4})?$/.test(value);
	if (isZip(a) && isZip(b)) {
		return a.slice(0, 5) === b.slice(0, 5);
	}
	return a === b;
}

// ─── The outcome ──────────────────────────────────────────────────────────

/**
 * Why a stored access request is verified or not. The visitor is never told which; staff
 * read it on the submission and in the salesperson's notice.
 *
 * - `verified`: enough proofs passed and the customer allows verified access requests.
 * - `not_found`: the customer number names no active customer of the brand's company.
 * - `too_few_proofs`: fewer proofs than required were given (none were wrong).
 * - `proofs_off`: the form has Create logins with proof off, so it takes no proofs and every
 *   request for a customer it finds goes to the salesperson.
 * - `switch_off`: the proofs passed, but the customer has Allow verified access requests off.
 * - `too_many_attempts`: the attempt that hit the limit of failed attempts.
 * - `spam_check_unavailable`: Cloudflare could not be reached, so no proof was checked.
 * - `duplicate`: the same request again within the form's duplicate window.
 */
export const ACCESS_REQUEST_REASONS = [
	'verified',
	'not_found',
	'too_few_proofs',
	'proofs_off',
	'switch_off',
	'too_many_attempts',
	'spam_check_unavailable',
	'duplicate',
] as const;

export type AccessRequestReason = (typeof ACCESS_REQUEST_REASONS)[number];

export const ACCESS_REQUEST_REASON_LABELS: Readonly<
	Record<AccessRequestReason, string>
> = {
	verified: 'Verified',
	not_found: 'Customer number not found',
	too_few_proofs: 'Too few proofs',
	proofs_off: 'This form does not ask for proofs',
	switch_off: 'The customer does not allow verified access requests',
	too_many_attempts: 'Too many failed attempts',
	spam_check_unavailable: 'The spam check could not be completed',
	duplicate: 'Sent again within a few minutes',
};

/** One failed try for the same customer number before this request was stored. */
export interface AccessRequestFailedAttemptModel {
	/** ISO time, server clock. */
	at: string;
	/** The proofs given that did not check out (never their values). */
	proofs: Array<AccessProofKind>;
}

/**
 * PD-17590 — the outcome of an access request, stored on the submission
 * (`form_submission.accessRequest`) and never sent to the visitor. The proofs themselves
 * are never stored, only which kinds passed.
 */
export interface FormSubmissionAccessRequestModel {
	customerNumber: string;
	/** The customer the number names in the brand's company; null when none. */
	claimedClientId: number | null;
	/** Its name when the request arrived, so the inbox reads even if it changes later. */
	claimedClientName: string | null;
	verified: boolean;
	reason: AccessRequestReason;
	proofsOffered: Array<AccessProofKind>;
	proofsRequired: number;
	proofsPassed: Array<AccessProofKind>;
	/** Failed tries for this customer number in the window before this one. */
	failedAttempts: Array<AccessRequestFailedAttemptModel>;
}

/** What the visitor is told after an access request is stored. */
export type AccessRequestVisitorOutcome = 'login_on_its_way' | 'received';

/**
 * The visitor's wording for each stored outcome. A verified request says the login is
 * coming; every other stored request says the same "received", whatever the reason.
 */
export const ACCESS_REQUEST_VISITOR_MESSAGES: Readonly<
	Record<AccessRequestVisitorOutcome | 'received_after_attempts', string>
> = {
	login_on_its_way:
		'Your login is on its way. Check your email for a link to set your password.',
	received:
		"We've sent your request to our team. They will confirm your account with you before setting up your login.",
	received_after_attempts:
		"We couldn't verify those details, so we've sent your request to our team. They will confirm your account with you before setting up your login.",
};

/** What the Send code button is told, whether or not the customer number matched. */
export const ACCESS_CODE_SENT_MESSAGES: Readonly<
	Record<'email' | 'phone', string>
> = {
	email:
		'If that customer number is on file, a code is on its way to the email address we have for the account. It works for 10 minutes.',
	phone:
		'If that customer number is on file, we are sending a code to the phone number we have for the account, by text or a phone call. It works for 10 minutes.',
};

/** A code as typed: digits only, so "123 456" and "123-456" read as "123456". */
export function normalizeAccessCode(value: string): string | null {
	const digits = value.replace(/\D/g, '');
	return digits.length === ACCESS_CODE_LENGTH ? digits : null;
}
